import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Loader2, ExternalLink, ChevronRight, CheckCircle2, AlertTriangle, Zap, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Admin-only Evidence-Verification queue. Non-admins see 403.
// Reads /admin/verify/queue + /admin/verify/stats. Clicking a row opens
// /admin/verify/<degree_id> for the researcher form.
const COUNTRY_LABEL = {
  uk: "United Kingdom", us: "United States", de: "Germany", fr: "France",
  nl: "Netherlands", ie: "Ireland", sg: "Singapore", ae: "UAE",
  it: "Italy", es: "Spain", ca: "Canada", ch: "Switzerland",
  au: "Australia", nz: "New Zealand", in: "India", kr: "South Korea",
  hk: "Hong Kong", jp: "Japan", cn: "China", se: "Sweden",
  dk: "Denmark", no: "Norway", fi: "Finland", be: "Belgium",
};

export default function AdminVerify() {
  const { user, login } = useAuth();
  const [state, setState] = useState({ loading: true, forbidden: false, items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [filters, setFilters] = useState({ country: "", source_tier: "editorial", q: "", skip: 0, limit: 50 });
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const [q, s, cov] = await Promise.all([
        api.get("/admin/verify/queue", { params }),
        api.get("/admin/verify/stats").catch(() => ({ data: null })),
        api.get("/admin/verify/website-coverage").catch(() => ({ data: null })),
      ]);
      setState({ loading: false, forbidden: false, items: q.data?.items || [], total: q.data?.total || 0 });
      setSelected(new Set());   // clear selection on any reload / filter change
      if (s.data) setStats(s.data);
      if (cov.data) setCoverage(cov.data);
    } catch (err) {
      const status = err?.response?.status;
      setState({ loading: false, forbidden: status === 401 || status === 403, items: [], total: 0 });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.country, filters.source_tier, filters.skip]);

  if (state.loading && !state.items.length) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-16 text-stone-500 font-mono text-sm" data-testid="admin-verify-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
        Loading verification queue…
      </div>
    );
  }

  if (state.forbidden) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="admin-verify-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Admin access required</h1>
          <p className="mt-2 text-stone-600 text-sm">
            The verification queue is restricted to editorial researchers on the admin allowlist.
          </p>
          {!user && (
            <Button className="mt-4" onClick={login} data-testid="admin-verify-signin-btn">
              Sign in with Google
            </Button>
          )}
        </div>
      </div>
    );
  }

  const pct = stats && stats.total ? Math.round((stats.verified / stats.total) * 100) : 0;
  const editorialPct = stats && stats.editorial_total ? Math.round((stats.editorial_verified / stats.editorial_total) * 100) : 0;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-verify-page">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="font-editorial text-4xl">Evidence Verification</h1>
          <span className="text-xs font-mono text-stone-500 uppercase tracking-wider px-2 py-0.5 border border-stone-300 rounded">
            Researcher tools
          </span>
        </div>
        <p className="mt-1 text-stone-600 text-sm">
          Patch records against official admissions pages. Every save stamps the record with a Verified pill and clears the AI-interpretation cache.
        </p>
      </header>

      {stats && (
        <div className="mb-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-verify-stats">
          <StatCard label="Total records" value={stats.total.toLocaleString()} />
          <StatCard label="Verified" value={`${stats.verified.toLocaleString()} (${pct}%)`} accent />
          <StatCard label="Editorial-tier" value={stats.editorial_total.toLocaleString()} />
          <StatCard label="Editorial verified" value={`${stats.editorial_verified.toLocaleString()} (${editorialPct}%)`} accent />
        </div>
      )}

      {coverage?.by_country?.length > 0 && (
        <div className="mb-8" data-testid="admin-verify-coverage-panel">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs uppercase tracking-wider text-stone-500 font-mono">
              University website coverage · AI-Fetch readiness
            </h2>
            <span className="text-[10px] text-stone-400 font-mono">
              Low % → extend `derive_*_websites.py` OVERRIDES before running a batch
            </span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-verify-coverage-chips">
            {coverage.by_country.map((c) => {
              const p = c.coverage_pct;
              const tone =
                p >= 95 ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : p >= 75 ? "border-amber-300 bg-amber-50 text-amber-800"
                : "border-rose-300 bg-rose-50 text-rose-800";
              return (
                <span
                  key={c.country}
                  className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-xs ${tone}`}
                  title={`${c.with_website}/${c.total} universities have a website URL`}
                  data-testid={`admin-verify-coverage-chip-${c.country}`}
                >
                  <span className="font-medium">{c.country}</span>
                  <span className="font-mono">{p}%</span>
                  <span className="text-[10px] opacity-70">
                    ({c.with_website}/{c.total})
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <FreshnessQueuePanel />

      <AnnualReNarratePanel />

      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="admin-verify-filters">
        <select
          value={filters.country}
          onChange={(e) => setFilters({ ...filters, country: e.target.value, skip: 0 })}
          className="h-9 px-3 border border-stone-300 rounded-md text-sm bg-white"
          data-testid="admin-verify-country-select"
        >
          <option value="">All countries</option>
          {Object.entries(COUNTRY_LABEL).map(([cc, name]) => (
            <option key={cc} value={cc}>{name} ({cc.toUpperCase()})</option>
          ))}
        </select>
        <select
          value={filters.source_tier}
          onChange={(e) => setFilters({ ...filters, source_tier: e.target.value, skip: 0 })}
          className="h-9 px-3 border border-stone-300 rounded-md text-sm bg-white"
          data-testid="admin-verify-tier-select"
        >
          <option value="editorial">Editorial only</option>
          <option value="">All tiers</option>
        </select>
        <form
          className="flex gap-2 flex-1 min-w-[200px]"
          onSubmit={(e) => { e.preventDefault(); load(); }}
        >
          <Input
            placeholder="Search title or ID…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="h-9 text-sm"
            data-testid="admin-verify-search-input"
          />
          <Button type="submit" size="sm" data-testid="admin-verify-search-btn">Search</Button>
        </form>
      </div>

      {stats && (
        <p className="text-xs text-stone-500 mb-3 font-mono" data-testid="admin-verify-total-line">
          Showing {state.items.length} of {state.total.toLocaleString()} unverified records.
        </p>
      )}

      <BulkApproveBar
        items={state.items}
        selected={selected}
        setSelected={setSelected}
        bulkBusy={bulkBusy}
        onApprove={async () => {
          const ids = Array.from(selected);
          if (!ids.length) return;
          if (!window.confirm(
            `Bulk-approve ${ids.length} record${ids.length === 1 ? "" : "s"} as verified? `
            + "Each row keeps its existing evidence URL (Serper HEAD-checked 200) and gets "
            + "stamped with your admin email."
          )) return;
          setBulkBusy(true);
          try {
            const r = await api.post("/admin/verify/bulk-approve", { ids });
            toast.success(`Verified ${r.data.approved} of ${r.data.requested} records`);
            await load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk approve failed");
          } finally {
            setBulkBusy(false);
          }
        }}
        onUndo={async () => {
          if (!window.confirm(
            "Revert the most recent bulk-approve batch (rows verified within the last 60s window)? "
            + "This only touches rows stamped 'admin-bulk-approve' — manual verifications are safe."
          )) return;
          setBulkBusy(true);
          try {
            const r = await api.post("/admin/verify/undo-bulk-approve");
            if (r.data.undone === 0) {
              toast.info(r.data.message || "Nothing to undo");
            } else {
              toast.success(`Reverted ${r.data.undone} record${r.data.undone === 1 ? "" : "s"}`);
            }
            await load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Undo failed");
          } finally {
            setBulkBusy(false);
          }
        }}
      />

      <div className="overflow-hidden border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-queue-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 w-8">
                <input
                  type="checkbox"
                  data-testid="admin-verify-select-all"
                  checked={state.items.length > 0 && selected.size === state.items.length}
                  ref={(el) => {
                    if (el) el.indeterminate = selected.size > 0 && selected.size < state.items.length;
                  }}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(new Set(state.items.map((d) => d.id)));
                    else setSelected(new Set());
                  }}
                  className="h-3.5 w-3.5 cursor-pointer"
                />
              </th>
              <th className="text-left px-4 py-2">University</th>
              <th className="text-left px-4 py-2">Programme</th>
              <th className="text-left px-3 py-2">Evidence</th>
              <th className="text-left px-4 py-2">Tier</th>
              <th className="text-right px-4 py-2">Offer</th>
              <th className="text-right px-4 py-2">Tuition (USD)</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {state.items.map((d) => {
              const badge = _evidenceBadge(d);
              const isSelected = selected.has(d.id);
              return (
                <tr
                  key={d.id}
                  className={`border-t border-stone-100 hover:bg-stone-50 ${isSelected ? "bg-emerald-50/40" : ""}`}
                  data-testid={`admin-verify-row-${d.id}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      data-testid={`admin-verify-select-${d.id}`}
                      checked={isSelected}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(d.id);
                          else next.delete(d.id);
                          return next;
                        });
                      }}
                      className="h-3.5 w-3.5 cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-stone-500">{d.university_id}</td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/admin/verify/${encodeURIComponent(d.id)}`}
                      className="text-stone-900 hover:underline"
                    >
                      {d.official_title || d.title || d.name || d.id}
                      {d.qualification ? <span className="text-stone-400"> — {d.qualification}</span> : null}
                      {!d.official_title && !d.title && d.name && (
                        <span className="ml-2 text-[10px] font-underground-sc text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                          overview
                        </span>
                      )}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <EvidenceBadge badge={badge} evidence={d.record_evidence} />
                  </td>
                  <td className="px-4 py-2 text-xs text-stone-500">{d.source_tier || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {d?.admissions?.offer_rate != null ? `${Math.round(d.admissions.offer_rate * 100)}%` : "—"}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {d.tuition_international_usd != null ? `$${d.tuition_international_usd.toLocaleString()}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/admin/verify/${encodeURIComponent(d.id)}`}
                      className="inline-flex items-center gap-1 text-stone-700 hover:text-stone-900"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </td>
                </tr>
              );
            })}
            {state.items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-stone-500 text-sm">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600 inline mr-2" />
                  Nothing left in this queue — all matching records are verified.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {state.total > filters.limit && (
        <div className="mt-4 flex items-center justify-between text-sm" data-testid="admin-verify-pagination">
          <Button
            variant="outline"
            size="sm"
            disabled={filters.skip === 0}
            onClick={() => setFilters({ ...filters, skip: Math.max(0, filters.skip - filters.limit) })}
            data-testid="admin-verify-prev-btn"
          >
            Previous
          </Button>
          <span className="text-stone-500 font-mono text-xs">
            {filters.skip + 1}–{Math.min(filters.skip + filters.limit, state.total)} of {state.total.toLocaleString()}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={filters.skip + filters.limit >= state.total}
            onClick={() => setFilters({ ...filters, skip: filters.skip + filters.limit })}
            data-testid="admin-verify-next-btn"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`p-4 border rounded-lg ${accent ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-white"}`}>
      <div className="text-xs uppercase tracking-wider text-stone-500 font-mono">{label}</div>
      <div className="text-2xl font-editorial mt-1 text-stone-900">{value}</div>
    </div>
  );
}


/**
 * Compute an evidence-strength badge for the queue row so admins can
 * eyeball at a glance whether a record is safe to bulk-approve.
 *
 * Returns { tone: 'strong'|'medium'|'weak'|'missing', label, hint }
 */
function _evidenceBadge(d) {
  const ev = d?.record_evidence || {};
  const url = ev.url || ev.source_url;
  const hasData =
    d?.admissions?.offer_rate != null ||
    d?.tuition_international_usd != null ||
    d?.cost?.tuition_domestic_annual_gbp != null;
  if (!url) {
    return { tone: "missing", label: "No URL", hint: "No source URL captured — needs research first." };
  }
  if (ev.verified_status === 200 && hasData) {
    return {
      tone: "strong",
      label: "Live + data",
      hint: `Source URL responded 200 · row already has offer/tuition data. Safe to bulk-approve.`,
    };
  }
  if (ev.verified_status === 200) {
    return {
      tone: "medium",
      label: "URL live",
      hint: "URL is reachable but row has no offer/tuition yet — waiting on AI Fetch, not human review.",
    };
  }
  return {
    tone: "weak",
    label: "URL untested",
    hint: "URL captured but no HEAD verification recorded — click through to research.",
  };
}


function EvidenceBadge({ badge, evidence }) {
  const url = evidence?.url || evidence?.source_url;
  const tones = {
    strong:  "border-emerald-300 bg-emerald-50 text-emerald-900",
    medium:  "border-amber-300  bg-amber-50  text-amber-900",
    weak:    "border-stone-300  bg-stone-50  text-stone-700",
    missing: "border-rose-300   bg-rose-50   text-rose-800",
  };
  return (
    <span
      title={badge.hint + (url ? ` · ${url}` : "")}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-[1px] text-[10px] font-underground-sc tracking-wider ${tones[badge.tone]}`}
      data-testid={`admin-verify-evidence-${badge.tone}`}
    >
      {badge.label}
      {url && (
        <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-0.5 opacity-70 hover:opacity-100">
          <ExternalLink className="h-2.5 w-2.5" />
        </a>
      )}
    </span>
  );
}


/**
 * Toolbar shown above the queue. Summarises selection + offers "select
 * all with strong evidence" shortcut so admins can dispatch the safe
 * pile in two clicks.
 */
function BulkApproveBar({ items, selected, setSelected, bulkBusy, onApprove, onUndo }) {
  const strongIds = items.filter((d) => _evidenceBadge(d).tone === "strong").map((d) => d.id);
  const mediumIds = items.filter((d) => _evidenceBadge(d).tone === "medium").map((d) => d.id);
  return (
    <div
      data-testid="admin-verify-bulk-bar"
      className={`mb-3 rounded-lg border px-3 py-2 flex flex-wrap items-center gap-2 transition ${
        selected.size > 0
          ? "border-emerald-300 bg-emerald-50/60"
          : "border-stone-200 bg-stone-50"
      }`}
    >
      <span className="text-xs text-stone-600 font-mono" data-testid="admin-verify-bulk-count">
        {selected.size > 0 ? (
          <span className="text-emerald-800 font-semibold">
            {selected.size} selected
          </span>
        ) : (
          "Select rows to bulk-approve"
        )}
      </span>
      <span className="text-stone-300">·</span>
      <button
        type="button"
        onClick={() => setSelected(new Set(strongIds))}
        disabled={strongIds.length === 0}
        className="text-xs font-mono text-stone-700 hover:text-emerald-800 underline decoration-dotted underline-offset-2 disabled:opacity-40 disabled:no-underline"
        data-testid="admin-verify-select-strong"
      >
        Select {strongIds.length} strong-evidence rows
      </button>
      <span className="text-stone-300">·</span>
      <button
        type="button"
        onClick={() => setSelected(new Set([...strongIds, ...mediumIds]))}
        disabled={strongIds.length + mediumIds.length === 0}
        className="text-xs font-mono text-stone-700 hover:text-amber-800 underline decoration-dotted underline-offset-2 disabled:opacity-40 disabled:no-underline"
        data-testid="admin-verify-select-strong-medium"
      >
        + medium ({strongIds.length + mediumIds.length} total)
      </button>
      <span className="ml-auto flex items-center gap-2">
        {selected.size > 0 && (
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs font-mono text-stone-500 hover:text-stone-800"
            data-testid="admin-verify-clear-selection"
          >
            Clear
          </button>
        )}
        {onUndo && (
          <button
            type="button"
            onClick={onUndo}
            disabled={bulkBusy}
            data-testid="admin-verify-undo-last"
            title="Revert the most recent bulk-approve batch (60s window)."
            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-underground-sc tracking-wider px-2.5 py-1 transition disabled:opacity-50"
          >
            <Undo2 className="w-3 h-3" />
            Undo last
          </button>
        )}
        <Button
          size="sm"
          onClick={onApprove}
          disabled={selected.size === 0 || bulkBusy}
          data-testid="admin-verify-bulk-approve"
          className="h-8"
        >
          {bulkBusy ? (
            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Approving…</>
          ) : (
            <><Zap className="w-3 h-3 mr-1" /> Approve {selected.size || ""}</>
          )}
        </Button>
      </span>
    </div>
  );
}



/**
 * FreshnessQueuePanel — surface the "verification due" backlog: items
 * where a background daemon wanted to overwrite an admin/uni-edited
 * value but the delta exceeded threshold. Admins accept (apply new
 * value) or dismiss (keep current).
 */
function FreshnessQueuePanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/verify/freshness-queue", { params: { status: "pending", limit: 25 } });
      setRows(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch { /* silent — admin will see the empty state */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setActing(id);
    try {
      await api.post(`/admin/verify/freshness-queue/${id}/${action}`, {});
      toast.success(action === "accept" ? "Applied — degree updated" : "Dismissed — kept current value");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setActing(null);
    }
  };

  if (!loading && total === 0) return null;

  return (
    <div className="mb-6 border border-amber-200 bg-amber-50/60 rounded-lg overflow-hidden" data-testid="admin-freshness-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="admin-freshness-toggle"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700" />
          <span className="font-medium text-amber-900">
            Verification due · {total} field{total === 1 ? "" : "s"} awaiting review
          </span>
        </div>
        <ChevronRight className={`w-4 h-4 text-amber-700 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-amber-200">
          {rows.length === 0 ? (
            <div className="p-4 text-xs text-stone-500 italic">Nothing pending.</div>
          ) : (
            <ul className="divide-y divide-amber-200/60">
              {rows.map((r) => (
                <li key={r.id} className="p-3 flex items-start gap-3" data-testid={`freshness-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-stone-900 truncate">
                      <Link to={`/admin/verify/${encodeURIComponent(r.degree_id)}`} className="hover:underline">
                        {r.degree_id}
                      </Link>
                      <span className="text-stone-500"> · {r.field_path}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-stone-600">
                      <span className="font-mono">{String(r.current_value ?? "—")}</span>
                      <span className="mx-1.5 text-stone-400">→</span>
                      <span className="font-mono text-amber-900">{String(r.proposed_value ?? "—")}</span>
                      {r.delta_pct != null && (
                        <span className="ml-2 text-[10px] font-underground-sc tracking-wider text-amber-700">
                          {r.delta_pct.toFixed(1)}% delta
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[10px] text-stone-500 font-mono">
                      {r.source_key || "source"}
                      {r.editor ? ` · edited by ${r.editor}` : ""}
                      {r.created_at ? ` · queued ${new Date(r.created_at).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(r.id, "accept")}
                      disabled={acting === r.id}
                      data-testid={`freshness-accept-${r.id}`}
                      className="h-7 text-xs"
                    >
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => act(r.id, "dismiss")}
                      disabled={acting === r.id}
                      data-testid={`freshness-dismiss-${r.id}`}
                      className="h-7 text-xs text-stone-500"
                    >
                      Dismiss
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * AnnualReNarratePanel — trigger a batch of AI re-narration runs.
 * Each proposed section is gated by queue_or_write, so admin/school-
 * edited sections queue for review instead of getting silently rewritten.
 */
function AnnualReNarratePanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [limit, setLimit] = useState(5);
  const [olderThanDays, setOlderThanDays] = useState(180);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/interpret/annual-refresh", {
        limit,
        older_than_days: olderThanDays,
      });
      setResult(r.data);
      toast.success(`Re-narration complete · ${r.data.written} written · ${r.data.queued} queued`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-narration failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mb-6 border border-indigo-200 bg-indigo-50/60 rounded-lg overflow-hidden" data-testid="admin-renarrate-panel">
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-indigo-900 flex items-center gap-2">
            Annual re-narration
            <span className="text-[10px] font-underground-sc tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200">
              Professor
            </span>
          </div>
          <p className="text-xs text-stone-600 mt-0.5">
            Regenerates the 6 flagship sections against fresh course data.
            School/admin-edited sections are queued instead of overwritten.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-[10px] font-underground-sc text-stone-500">Batch</label>
          <Input
            type="number"
            min={1}
            max={50}
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(50, Number(e.target.value) || 5)))}
            className="h-8 w-16 text-sm"
            data-testid="admin-renarrate-limit"
          />
          <label className="text-[10px] font-underground-sc text-stone-500">Older than (days)</label>
          <Input
            type="number"
            min={0}
            max={3650}
            value={olderThanDays}
            onChange={(e) => setOlderThanDays(Math.max(0, Math.min(3650, Number(e.target.value) || 0)))}
            className="h-8 w-20 text-sm"
            data-testid="admin-renarrate-days"
          />
          <Button
            size="sm"
            onClick={run}
            disabled={running}
            data-testid="admin-renarrate-run"
            className="h-8"
          >
            {running ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Running…</> : "Run batch"}
          </Button>
        </div>
      </div>
      {result && (
        <div className="border-t border-indigo-200 px-4 py-2 text-xs text-stone-700 font-mono flex items-center gap-4" data-testid="admin-renarrate-result">
          <span>scanned: <b>{result.scanned}</b></span>
          <span className="text-emerald-700">written: <b>{result.written}</b></span>
          <span className="text-amber-700">queued: <b>{result.queued}</b></span>
          <span className="text-stone-500">no-op: <b>{result.noop}</b></span>
          {result.errors?.length > 0 && (
            <span className="text-rose-700">errors: <b>{result.errors.length}</b></span>
          )}
        </div>
      )}
    </div>
  );
}
